/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { PropsWithChildren } from 'react';
import { Platform, View, TouchableOpacity, ViewStyle } from 'react-native';
import { ActivityIndicator, Button as PaperButton } from 'react-native-paper';
import color from 'color';
import Typography from './Typography';
import { UiDefaultIconName, UiIcon, UiIconType } from './SimpleListIcon';
import { useBicyclehutTheme } from '../utils/paperTheme';

type PaperButtonProps = React.ComponentProps<typeof PaperButton>;
export type ButtonProps = Omit<PaperButtonProps, 'icon'> & {
  textColor?: string;
  loading?: boolean;
  rounded?: boolean;
  narrow?: boolean;
  fixedHeight?: boolean;
  contentCentered?: boolean;
  icon?: UiIconType | UiDefaultIconName;
  rightIcons?: (UiIconType | UiDefaultIconName)[];
  contentContainerStyle?: ViewStyle;
};

const Button = ({
  children,
  textColor,
  loading,
  disabled,
  style,
  contentContainerStyle,
  rounded,
  icon,
  rightIcons,
  narrow,
  fixedHeight = true,
  contentCentered = true,
  ...otherProps
}: PropsWithChildren<ButtonProps>) => {
  const { roundness, dark, colors } = useBicyclehutTheme();
  const disabledBackgroundColor = color(dark ? '#ffffff' : '#000000')
    .alpha(0.12)
    .rgb()
    .string();

  return (
    <TouchableOpacity
      disabled={disabled || loading}
      style={[
        {
          width: '100%',
          marginLeft: 0,
          marginRight: 0,
          marginTop: 0,
          marginBottom: 0,
          backgroundColor: disabled || loading ? disabledBackgroundColor : '#ffffff00',
          padding: 6,
          display: 'flex',
          alignItems: 'center',
          borderRadius: rounded ? 24 : roundness,
          justifyContent: 'center',
          borderWidth: 1,
          borderColor: colors.primary,
        },
        fixedHeight ? { height: 47 } : { minHeight: 47 },
        style,
      ]}
      onPress={otherProps.onPress}
    >
      <View
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            paddingVertical: Platform.OS !== 'web' ? 4 : 0,
            paddingHorizontal: 10,
          },
          contentContainerStyle,
        ]}
      >
        {icon && rightIcons && (
          <UiIcon
            {...(typeof icon === 'string' ? { name: icon } : { ...icon })}
            size={24}
            color={colors.secondary}
            style={{ marginRight: 10 }}
          />
        )}
        <View
          style={[
            {
              flexDirection: 'row',
              alignItems: 'center',
              flexShrink: 1,
            },
            !rightIcons && contentCentered ? { marginHorizontal: 'auto' } : undefined,
          ]}
        >
          {/* Loading indicator, if loading */}
          {loading ? <ActivityIndicator size={16} style={{ marginRight: 10 }} color={colors.secondary} /> : null}

          {/* Button icon, if specified */}
          {icon && !rightIcons && (
            <UiIcon
              {...(typeof icon === 'string' ? { name: icon } : { ...icon })}
              size={24}
              color={colors.secondary}
              style={{ marginRight: 10, marginLeft: 'auto' }}
            />
          )}

          {/* Actual button content. */}
          <Typography variant={'button'} color={textColor ?? colors.primary} style={{ flexShrink: 1 }}>
            {children}
          </Typography>
        </View>

        {/* Icons aligned to the right, if specified */}
        {rightIcons?.length && (
          <View style={{ flexDirection: 'row', marginLeft: 'auto' }}>
            {rightIcons?.map((rightIcon) => (
              <UiIcon
                {...(typeof rightIcon === 'string' ? { name: rightIcon } : { ...rightIcon })}
                key={typeof rightIcon === 'string' ? rightIcon : rightIcon.name}
                size={24}
                color={colors.secondary}
              />
            ))}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};
export default Button;
